import "./new-employee-incident.scss";

import { useNavigate, useParams } from "react-router-dom";
import { zodResolver } from "@hookform/resolvers/zod";
import { FieldErrors, FormProvider, useForm } from "react-hook-form";
import { FaChevronLeft } from "react-icons/fa6";
import { Button } from "primereact/button";

import { MessageResponseDto } from "@api/api";
import { useFormApiMapper } from "@core/services/form-api-mapper.hook";
import { useQyCreateEmployeeIncident } from "@core/query/employee-incident.query";
import {
  EmployeeAttachmentFormSchema,
  EmployeeIncidentFormRule,
  EmployeeIncidentFormSchema,
} from "@core/form/form.rule";
import { employeeIncidentFormDefault } from "@core/form/form.default";
import { getFormErrorMessage } from "@core/utility/get-error-message";
import { useNotificationContext } from "@shared/ui/notification/notification.context";
import HeaderContent from "@shared/ui/header-content/header-content";
import FormEmployeeIncident from "../form-employee-incident/form-employee-incident";
import { useFormDropdownPersonContext } from "@domain/person/form-dropdown-person/form-dropdown-person.context";
import { useQyCreateEmployeeAttachment } from "@core/query/employee-attachment.query";
import { AttachmentSource } from "@core/model/query-model-employee-attachment";

export function EmployeeIncident() {
  // LOCAL DECLARATION
  const navigate = useNavigate();
  const { selectedPersonData } = useFormDropdownPersonContext();
  const { mapCreateEmployeeIncident, mapCreateEmployeeAttachment } =
    useFormApiMapper();
  const { showError, showSuccess } = useNotificationContext();
  const { branchId } = useParams();

  // QUERIES
  // API ATTACHMENT
  const { mutate: addEmployeeAttachment } = useQyCreateEmployeeAttachment();

  const handleApiSuccess = (response: MessageResponseDto) => {
    const data = response.data as any;

    if (typeof fileInput === "object") {
      const mutatedForm = {
        userCode: selectedPersonData?.code,
        bucketCode: selectedPersonData?.bucket.code,
        isActive: true,
        source: AttachmentSource.Incident,
        file: fileInput,
        requestCode: data.code,
      } as EmployeeAttachmentFormSchema;

      const formData = mapCreateEmployeeAttachment(mutatedForm);
      addEmployeeAttachment(formData);
    }

    showSuccess(`New EmployeeIncident is created`);
    navigate(`../`);
  };
  const { mutate: addEmployeeIncident, isLoading: isCreating } =
    useQyCreateEmployeeIncident(handleApiSuccess);

  // LOCAL FUNCTIONS
  const formMethod = useForm<EmployeeIncidentFormSchema>({
    defaultValues: employeeIncidentFormDefault,
    resolver: zodResolver(EmployeeIncidentFormRule),
  });
  const { handleSubmit, watch } = formMethod;
  const fileInput = watch("file");

  const handleValidate = (form: EmployeeIncidentFormSchema) => {
    const mutatedForm = {
      ...form,
      userCode: selectedPersonData?.code,
      bucketCode: selectedPersonData?.bucket.code,
      branchCode: branchId,
    } as EmployeeIncidentFormSchema;
    const formData = mapCreateEmployeeIncident(mutatedForm);
    addEmployeeIncident(formData);
  };
  const handleValidateError = (
    err: FieldErrors<EmployeeIncidentFormSchema>
  ) => {
    const formMessage = getFormErrorMessage(err);
    showError(formMessage);
  };

  return (
    <div id="EmployeeIncident" className="employee-incident">
      <HeaderContent
        title="New Employee Incident"
        backIcon={<FaChevronLeft />}
        onBack={() => navigate("../")}
      >
        <Button
          className="w-full block"
          label="Save"
          disabled={isCreating}
          onClick={handleSubmit(handleValidate, handleValidateError)}
        ></Button>
      </HeaderContent>

      <div className="p-7">
        <FormProvider {...formMethod}>
          <section className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-4 bg-white">
            <FormEmployeeIncident />
          </section>
        </FormProvider>
      </div>
    </div>
  );
}

export default EmployeeIncident;
